/**
 * Content-engine product tiers — the architectural backbone for "how the engine
 * varies by user" (implementation plan §2). The Board, idea discovery, guardrail,
 * and scheduling are ONE engine; this capability config is the only thing that
 * changes per user type.
 *
 * ⚠️ THIS PARAGRAPH USED TO SAY franchise/enterprise "require the org model
 * (organizations + members + brand-kit inheritance + RLS), which is Phase 1".
 * That is stale twice over: workspace-tenancy Phase 4 RETIRED
 * `organizations`/`org_members` (they do not exist in prod), and tier is now
 * derived from the BRAND-KIT HIERARCHY instead — see `useTier()` (client) and
 * `resolveTierForUser()` (server), which both resolve franchise and enterprise
 * for real. As of 2026-09-06 prod holds 34 brand kits, 1 of them with a parent.
 */

export type Tier = 'consumer' | 'franchise' | 'enterprise';

export interface TierCapabilities {
  tier: Tier;
  /** Where the brand kit comes from. */
  brandSource: 'own' | 'inherited' | 'authored';
  /**
   * Guardrail strictness. `soft` = the user owns the brand and can override
   * anything (consumer). `hard` = HQ-locked fields (logo/color/disclaimer) are
   * immutable (franchise/enterprise).
   */
  guardrail: 'soft' | 'hard';
  /** Distribution reach for fan-out. */
  fanOutScope: 'channels' | 'stores' | 'all-locations';
  /** Weather / local-event signals in the idea feed. */
  localSignals: boolean;
  /** Internal review of teammate-created content. */
  teamApproval: 'none' | 'optional' | 'mandated';
}

export const TIER_CAPABILITIES: Record<Tier, TierCapabilities> = {
  // Solo creator (Sarah, Shopify store). Owns her brand; soft guardrail; her channels only.
  consumer: {
    tier: 'consumer',
    brandSource: 'own',
    guardrail: 'soft',
    fanOutScope: 'channels',
    localSignals: false,
    teamApproval: 'none',
  },
  // Franchise owner. Inherits the corporate brand kit (hard locks); fans out to his
  // own stores; optional internal team approval; local signals on. (Phase 1.)
  franchise: {
    tier: 'franchise',
    brandSource: 'inherited',
    guardrail: 'hard',
    fanOutScope: 'stores',
    localSignals: true,
    teamApproval: 'optional',
  },
  // Corporate brand owner. Authors the kit + campaign moments pushed to locations;
  // may mandate review. (Phase 5.)
  enterprise: {
    tier: 'enterprise',
    brandSource: 'authored',
    guardrail: 'hard',
    fanOutScope: 'all-locations',
    localSignals: true,
    teamApproval: 'mandated',
  },
};

/**
 * Synchronous tier resolution — ALWAYS `'consumer'`, unconditionally.
 *
 * 🔴 THIS IS A FALLBACK, NOT A TIER CHECK. It has no user, no workspace and no
 * query, so it cannot know a tier; it returns the cheapest one so a context
 * with no identity (edge fn, test, cron) fails safe. Gating a feature on it
 * silently disables that feature for FRANCHISE AND ENTERPRISE TOO, and nothing
 * about the call site will look wrong.
 *
 * For a real tier use `useTier()` (`@/hooks/useTier`) in React, or
 * `resolveTierForUser(supabase, accountId)`
 * (`@/lib/content-engine/resolve-tier-server`) anywhere else — both derive it
 * from the brand-kit hierarchy: a kit with `parent_brand_kit_id` is a franchise
 * location, a kit referenced as another kit's parent is enterprise.
 *
 * Inside the agent turn there is a third, cheaper answer already computed:
 * `brandData.org_governance` is populated only for those two cases, so absence
 * means consumer. `generate_campaign_flow`'s gate uses it (adk-agent.ts).
 */
export function resolveTier(): Tier {
  return 'consumer';
}

export function getTierCapabilities(tier: Tier = resolveTier()): TierCapabilities {
  return TIER_CAPABILITIES[tier];
}
